import type { MantineColorsTuple } from "@mantine/core";

export type BrandPaletteKey = "teal" | "gold" | "purple" | "green" | "blue";

export interface BrandPalette {
  key: BrandPaletteKey;
  label: string;
  description: string;
  primary: string;
  onPrimary: string;
  accent: string;
  ink: string;
  surface: string;
  muted: string;
  colors: MantineColorsTuple;
}

export const DEFAULT_BRAND_PALETTE: BrandPaletteKey = "teal";
export const BRAND_PALETTE_STORAGE_KEY = "evolys-brand-palette";
export const BRAND_PALETTE_COOKIE_KEY = "evolys_brand_palette";

export const BRAND_PALETTES: Record<BrandPaletteKey, BrandPalette> = {
  teal: {
    key: "teal",
    label: "Clinical teal",
    description: "Calm, modern and familiar across the current clinical workspace.",
    primary: "#0EA39A",
    onPrimary: "#0B1F3A",
    accent: "#6ED6D1",
    ink: "#0B1F3A",
    surface: "#FFFFFF",
    muted: "#F2F4F7",
    colors: [
      "#e8fbfa",
      "#c8f3f0",
      "#9fe7e2",
      "#6ed6d1",
      "#36beb7",
      "#0ea39a",
      "#0a8881",
      "#086d68",
      "#075651",
      "#043936",
    ],
  },
  gold: {
    key: "gold",
    label: "Gold & navy",
    description: "Premium, established and closest to the gold identity board.",
    primary: "#D4A017",
    onPrimary: "#0B1F3A",
    accent: "#F5B23A",
    ink: "#0B1F3A",
    surface: "#FFFFFF",
    muted: "#F2F4F7",
    colors: [
      "#fff8e6",
      "#ffedbd",
      "#fce08e",
      "#f5c956",
      "#f5b23a",
      "#d4a017",
      "#b2820f",
      "#8e660a",
      "#694a06",
      "#463103",
    ],
  },
  purple: {
    key: "purple",
    label: "Precision purple",
    description: "Distinctive and technology-forward with a deep navy anchor.",
    primary: "#6F42C1",
    onPrimary: "#FFFFFF",
    accent: "#A78BFA",
    ink: "#0B1F3A",
    surface: "#FFFFFF",
    muted: "#F2F4F7",
    colors: [
      "#f4efff",
      "#e4d9ff",
      "#cebaff",
      "#b99dfc",
      "#a78bfa",
      "#8b62df",
      "#6f42c1",
      "#59339e",
      "#432677",
      "#2d1952",
    ],
  },
  green: {
    key: "green",
    label: "Health green",
    description: "Natural, reassuring and grounded in patient care.",
    primary: "#2E7D32",
    onPrimary: "#FFFFFF",
    accent: "#A5D6A7",
    ink: "#1F2933",
    surface: "#FFFFFF",
    muted: "#F2F4F7",
    colors: [
      "#edf8ee",
      "#d5edd7",
      "#b9dfbc",
      "#a5d6a7",
      "#78bb7c",
      "#4c9c51",
      "#2e7d32",
      "#246428",
      "#194a1d",
      "#0f3113",
    ],
  },
  blue: {
    key: "blue",
    label: "Diagnostic blue",
    description: "Clear, dependable and traditionally clinical.",
    primary: "#1E63DD",
    onPrimary: "#FFFFFF",
    accent: "#4DAFFA",
    ink: "#0B1F3A",
    surface: "#FFFFFF",
    muted: "#F2F4F7",
    colors: [
      "#edf5ff",
      "#d8eaff",
      "#b8d8ff",
      "#8fc2ff",
      "#4daffa",
      "#2b83ed",
      "#1e63dd",
      "#174db4",
      "#103989",
      "#09265f",
    ],
  },
};

export const BRAND_PALETTE_OPTIONS = (
  Object.keys(BRAND_PALETTES) as BrandPaletteKey[]
).map((key) => BRAND_PALETTES[key]);

export function isBrandPaletteKey(value: string | null | undefined): value is BrandPaletteKey {
  return Boolean(value && value in BRAND_PALETTES);
}
